import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import { AuthProvider } from './auth/AuthProvider';
import { msalInstance, initializeMsal } from './auth/config';
import { ErrorBoundary, ToastProvider } from './components/error';
import { ErrorCodes, ErrorSeverity } from '@shared/types/errors';
import { initializeTelemetry, trackException } from './services/telemetryService';

const telemetryConnectionString = import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING || '';

initializeTelemetry({
  connectionString: telemetryConnectionString,
  enableAutoRouteTracking: true,
  enableCorsCorrelation: true,
  samplingPercentage: import.meta.env.PROD ? 10 : 100,
  environment: import.meta.env.PROD ? 'prod' : 'dev',
});

/**
 * Global error handler for uncaught errors and promise rejections
 * Feature: 034-global-error-handling
 * FR-029: Capture and log unhandled global errors
 */
const setupGlobalErrorHandlers = (): void => {
  // Handle uncaught errors
  window.onerror = (message, source, lineno, colno, error): boolean => {
    const trackedError = error instanceof Error ? error : new Error(String(message));

    trackException(trackedError, {
      code: ErrorCodes.UNKNOWN_ERROR,
      message: String(message),
      severity: ErrorSeverity.CRITICAL,
      route: window.location.pathname,
    });

    console.error('[GlobalErrorHandler] Uncaught error:', {
      message,
      source,
      lineno,
      colno,
      error: error?.stack || error,
      timestamp: new Date().toISOString(),
    });
    // Return false to allow default error handling
    return false;
  };

  // Handle unhandled promise rejections
  window.onunhandledrejection = (event: PromiseRejectionEvent): void => {
    const reason = event.reason instanceof Error ? event.reason : new Error(String(event.reason));

    trackException(reason, {
      code: ErrorCodes.UNKNOWN_ERROR,
      message: reason.message,
      severity: ErrorSeverity.HIGH,
      route: window.location.pathname,
    });

    console.error('[GlobalErrorHandler] Unhandled promise rejection:', {
      reason: event.reason,
      timestamp: new Date().toISOString(),
    });
  };
};

// Set up global error handlers
setupGlobalErrorHandlers();

// Initialize MSAL before rendering
initializeMsal().then(() => {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <ErrorBoundary name="App" showReportButton={true}>
        <ToastProvider>
          <AuthProvider msalInstance={msalInstance}>
            <App />
          </AuthProvider>
        </ToastProvider>
      </ErrorBoundary>
    </React.StrictMode>
  );
});
